import { SearchOutlined } from '@ant-design/icons';
import { Flex, Input, Select, Space, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactTimeago from 'react-timeago';
import { useDebounce } from 'use-debounce';
import { useDamTransferServiceGetBackendv2Damtransfers, useUiMetaDataServiceGetBackendv2UimetadataBackendlist, useUiMetaDataServiceGetBackendv2UimetadataBackendlistKey } from '../../backendapi/queries';
import { DamTransferDTO } from '../../backendapi/requests';
import { ConfigContext } from '../../contexts/ConfigContext';
import { GenerateODataFilter } from '../../helpers/odataFunctions';
import { DAMTransferStatus, DAMTransferStatusLabels, DAMTransferStatusOptions } from '../../models/enums';
import { Filter, FilterGroup } from '../../types/types';

const DamTransferList: React.FC = () => {

    const { currentClient } = useContext(ConfigContext);

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebounce(query, 500);
    const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

    const filters = useMemo(() => {
        let filters: (Filter | FilterGroup)[] = [];

        if (debouncedQuery) {
            filters.push({ type: 'search', values: [{ value: debouncedQuery }], property: 'hostedfile.name', name: 'name' });
        }

        if (selectedStatuses.length > 0) {
            filters.push({ type: 'select', values: selectedStatuses.map(e => e === 1).map(e => ({ value: e })), property: 'isEnabled', name: 'isEnabled' });
        }

        return filters;
    }, [debouncedQuery, selectedStatuses, currentClient]);

    useEffect(() => {
        setPage(1);
    }, [currentClient, debouncedQuery, selectedStatuses, currentClient]);
    
    const handleStatusChange = useCallback((value: any, option?: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedStatuses(option.map(e => parseInt(e.value)));
        }
    }, []);

    const { data, isFetching, refetch } = useDamTransferServiceGetBackendv2Damtransfers({
        clientId: currentClient.id,
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    }, undefined, { refetchOnWindowFocus: false, placeholderData: (prev) => prev });

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<DamTransferDTO> | SorterResult<DamTransferDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const columns: ColumnsType<DamTransferDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Title',
            dataIndex: 'hostedfile.name',
            key: 'title',
            render: (_, value) => (value.hostedfile.name)
        },
        {
            title: 'Status',
            dataIndex: 'approvalStatus',
            key: 'approvalStatus',
            render: (_, value) => (DAMTransferStatusOptions[value.transferStatus as DAMTransferStatus])
        },
        {
            title: 'Error Message',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
        },
        {
            title: 'Created',
            dataIndex: 'timestampCreated',
            key: 'timestampCreated',
            render: (_, value) => (value.timestampCreated ? <ReactTimeago date={value.timestampCreated} /> : null),
        },
        {
            title: 'Started',
            dataIndex: 'timestampStarted',
            key: 'timestampStarted',
            render: (_, value) => (value.timestampStarted ? <ReactTimeago date={value.timestampStarted} /> : null),
        },
        {
            title: 'Completed',
            dataIndex: 'timestampCompleted',
            key: 'timestampCompleted',
            render: (_, value) => (value.timestampCompleted ? <ReactTimeago date={value.timestampCompleted} /> : null),
        },
    ];

    return (
        <>
            <Flex justify='space-between' style={{ marginBottom: 16 }}>

                <Space>
                    <Input prefix={<SearchOutlined />} placeholder='Search' value={query} onChange={e => setQuery(e.target.value)} />
                    <Select
                        mode="multiple"
                        placeholder="Status"
                        onChange={handleStatusChange}
                        popupMatchSelectWidth={false}
                        value={selectedStatuses.map(e => e.toString())}
                        size='middle'
                        style={{ minWidth: 100 }}
                        options={[
                            {
                                label: DAMTransferStatusLabels[DAMTransferStatus.Pending],
                                value: DAMTransferStatus.Pending.toString()
                            },
                        ]}
                    />
                </Space>
            </Flex>

            <Table
                loading={isFetching}
                dataSource={data?.value}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                showSorterTooltip={false}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    showQuickJumper: true,
                    showSizeChanger: true,
                }} />
        </>
    );
};

export default DamTransferList;